import React from 'react';
import PropTypes from 'prop-types';
import { both, complement, prop, either, path } from 'ramda';
import moment from 'moment';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import FormSection from 'containers/FormSectionContainer';
import FieldSet from 'containers/FieldSetContainer';
import FormField from 'containers/FormField';
import ComputedField from 'containers/ComputedField';
import ConditionalSection from 'containers/ConditionalSection';
import TextFieldArea from 'components/TextFieldArea';
import TextFieldBox from 'components/TextFieldBox';
import LocalizedValueField from 'components/LocalizedValueField';
import CheckboxGroup from 'components/CheckboxGroup';
import RadioButtonGroup from 'components/RadioButtonGroup';
import NumberFieldBox from 'components/NumberFieldBox';
import Datepicker from 'components/Datepicker';

import { oneOf, gte, lte, fieldExists, fieldIs, has, hasOne } from 'lib/conditions';
import { getGender } from 'lib/ssn';

import FormProvider from 'routes/Documents/containers/FormProvider';

import { formatDgDate } from 'lib/dateDisplay';
import { getLatestRankedDiagnosis } from 'lib/diagnosis';
import { getLatestFinishedCommitData } from 'lib/documentHandling';

import { optionAccessor, computeBMI } from './utils';
import { useNullifyDependentFields } from './hooks';
import { nowPartialDate } from 'neuro-utils';

const formatOpt = optionAccessor;

const pregnancyMaxDate = moment().add(9, 'months').toDate();

const DiagnosisDisplay = ({ diagnoses }) => {
  const latestDg = getLatestRankedDiagnosis(diagnoses);
  if (!latestDg) return null;

  return (
    <FormSection localeId="background.sections.diagnosis">
      <FieldSet localeId="background.labels.diagnosisClass">
        <FormField
          name="patientDiagnose"
          component={LocalizedValueField}
          isStatic
          value={`diagnosis.${latestDg.diagnosis}`}
        />
      </FieldSet>
      <FieldSet localeId="background.labels.diagnosisDate">
        <FormField name="patientDiagnoseDate" component={TextFieldBox} isStatic value={formatDgDate(latestDg.date)} />
      </FieldSet>
      {latestDg.type && (
        <FieldSet localeId="background.labels.diagnosisType">
          <FormField
            name="patientDiagnoseType"
            component={LocalizedValueField}
            isStatic
            value={`diagnosis.course.${latestDg.type}`}
          />
        </FieldSet>
      )}
    </FormSection>
  );
};

DiagnosisDisplay.propTypes = {
  diagnoses: PropTypes.array,
};

const Form = ({ session, dispatch, formValues, ...props }) => {
  useNullifyDependentFields({ dispatch, formValues });

  React.useEffect(() => {
    if (props.editing && Object.keys(formValues).length === 0) {
      dispatch(props.change('date', nowPartialDate()));
    }
  }, []);

  return (
    <React.Fragment>
      <FormSection localeId="background.sections.familyPrevalence">
        <FieldSet localeId="background.labels.familyPrevalence">
          <FormField
            name="familyPrevalence"
            component={RadioButtonGroup}
            optionFormatter={formatOpt}
            preset="yesnounknown"
          />
        </FieldSet>
        <ConditionalSection condition={fieldIs('familyPrevalence', 'yes')}>
          <FieldSet localeId="background.labels.familyPrevalenceDetail">
            <FormField
              name="familyPrevalenceDetail"
              component={CheckboxGroup}
              optionFormatter={formatOpt}
              options={[
                { value: 'siblings' },
                { value: 'twin' },
                { value: 'mother' },
                { value: 'father' },
                { value: 'children' },
                { value: 'otherRelative' },
              ]}
            />
          </FieldSet>
        </ConditionalSection>
      </FormSection>

      <FormSection localeId="background.sections.weightAndHeight">
        <FieldSet localeId="background.labels.height">
          <FormField
            name="patientLength"
            component={NumberFieldBox}
            hintLocale="background.labels.heightHint"
            min="0"
          />
        </FieldSet>
        <FieldSet localeId="background.labels.weight">
          <FormField
            name="patientWeight"
            component={NumberFieldBox}
            hintLocale="background.labels.weightHint"
            min="0"
          />
        </FieldSet>
        <ConditionalSection hideInEdit condition={both(gte('patientLength', 100), gte('patientWeight', 25))}>
          <FieldSet localeId="background.labels.bmi">
            <ComputedField component={NumberFieldBox} computation={computeBMI} />
          </FieldSet>
        </ConditionalSection>
      </FormSection>

      {/** @note Legacy: bpSyst, bpDiast */}
      <ConditionalSection condition={fieldExists('bpSyst') || fieldExists('bpDiast')}>
        <FormSection localeId="background.sections.bloodPressure">
          <FieldSet localeId="background.labels.bpSyst">
            <FormField name="bpSyst" component={NumberFieldBox} hintLocale="background.labels.bpSystHint" min="0" />
          </FieldSet>
          <FieldSet localeId="background.labels.bpDiast">
            <FormField name="bpDiast" component={NumberFieldBox} hintLocale="background.labels.bpDiastHint" min="0" />
          </FieldSet>
        </FormSection>
      </ConditionalSection>

      {(prop('citizedId', session) && getGender(prop('citizedId', session))) === 'female' && (
        <FormSection localeId="background.sections.childrenAndPregnancies">
          <FieldSet localeId="background.labels.numberOfChildren">
            <FormField
              name="numberOfChildren"
              component={NumberFieldBox}
              hintLocale="background.hints.numberOfChildren"
            />
          </FieldSet>
          <FieldSet localeId="background.labels.reproductiveStatus">
            <FormField
              name="reproductiveStatus"
              optionFormatter={formatOpt}
              component={RadioButtonGroup}
              options={[{ value: 'planning' }, { value: 'pregnant' }, { value: 'breastFeeding' }, { value: 'notOne' }]}
            />
          </FieldSet>
          <ConditionalSection condition={fieldIs('reproductiveStatus', 'pregnant')}>
            <FieldSet localeId="background.labels.pregnancyDueDate">
              <FormField name="pregnancyDueDate" component={Datepicker} maxDate={pregnancyMaxDate} />
            </FieldSet>
          </ConditionalSection>
          <FieldSet localeId="background.labels.usingContraceptive">
            <FormField name="usingContraceptive" component={RadioButtonGroup} preset="yesno" />
          </FieldSet>
          <ConditionalSection condition={fieldIs('usingContraceptive', 'yes')}>
            <FieldSet localeId="background.labels.contraceptives">
              <FormField
                name="contraceptives"
                optionFormatter={formatOpt}
                component={CheckboxGroup}
                options={[
                  { value: 'contraceptivePill' },
                  { value: 'miniPill' },
                  { value: 'vaginalRing' },
                  { value: 'contraceptivePatch' },
                  { value: 'contraceptiveInjection' },
                  { value: 'contraceptiveImplant' },
                  { value: 'IUS' },
                  { value: 'contraceptiveStick' },
                  { value: 'spermicide' },
                  { value: 'contraceptiveLube' },
                  { value: 'contraceptiveGel' },
                  { value: 'vaginalContraceptivePill' },
                  { value: 'maleCondom' },
                  { value: 'femaleCondom' },
                  { value: 'copperCoil' },
                  { value: 'Pessary' },
                  { value: 'sterilePartner' },
                  { value: 'naturalContraceptive' },
                  { value: 'withdrawal' },
                  { value: 'emergencyContraceptive' },
                  { value: 'lactationalAmenorrhea' },
                ]}
              />
            </FieldSet>
          </ConditionalSection>
        </FormSection>
      )}

      <FormSection localeId="background.sections.familyRelations">
        <FieldSet localeId="background.labels.maritialStatus">
          <FormField
            name="maritialStatus"
            component={RadioButtonGroup}
            optionFormatter={formatOpt}
            options={[
              { value: 'married' },
              { value: 'domesticPartnership' },
              { value: 'divorced' },
              { value: 'widow' },
              { value: 'single' },
            ]}
          />
        </FieldSet>
      </FormSection>

      <FormSection localeId="background.sections.living">
        <FieldSet localeId="background.labels.accomodation">
          <FormField
            name="accomodation"
            component={RadioButtonGroup}
            optionFormatter={formatOpt}
            options={[
              { value: 'independently' },
              { value: 'assistedLiving' },
              { value: 'shelteredHome' },
              { value: 'institutionalized' },
            ]}
          />
        </FieldSet>
      </FormSection>

      <FormSection localeId="background.sections.education">
        <FieldSet localeId="background.labels.baseEducation">
          <FormField
            name="baseEducation"
            component={RadioButtonGroup}
            optionFormatter={formatOpt}
            options={[{ value: 'elementary' }, { value: 'tradeSchool' }, { value: 'highSchool' }]}
          />
        </FieldSet>
        <FieldSet localeId="background.labels.furtherEducation">
          <FormField
            name="furtherEducation"
            component={RadioButtonGroup}
            optionFormatter={formatOpt}
            options={[
              { value: 'noVocationalEducation' },
              { value: 'vocationalCourseOrOnTheJobTraining' },
              { value: 'tradeSchoolOrApprenticeshipOrCompetenceBasedQualification' },
              { value: 'technicalSchool' },
              { value: 'technicalCollege' },
              { value: 'specialistVocationalQualification' },
              { value: 'polytechnic' },
              { value: 'bachelor' },
              { value: 'master' },
              { value: 'licentiate' },
              { value: 'phd' },
            ]}
          />
        </FieldSet>
        <FieldSet localeId="background.labels.educationYears" labelDetailId="background.infoText.educationYears">
          <FormField
            name="educationYears"
            component={NumberFieldBox}
            hintLocale="background.labels.educationYears"
            min="0"
            max="18"
          />
        </FieldSet>
      </FormSection>

      <FormSection localeId="background.sections.worklife">
        {/** @note Legacy: incomeClass */}
        <ConditionalSection condition={fieldExists('incomeClass')}>
          <FieldSet localeId="background.labels.incomeClass" labelDetailId="background.infoText.incomeClass">
            <FormField
              name="incomeClass"
              component={RadioButtonGroup}
              optionFormatter={formatOpt}
              options={[
                { value: 'incomeClass.1' },
                { value: 'incomeClass.2' },
                { value: 'incomeClass.3' },
                { value: 'incomeClass.4' },
                { value: 'incomeClass.5' },
                { value: 'incomeClass.6' },
                { value: 'incomeClass.7' },
                { value: 'incomeClass.8' },
                { value: 'incomeClass.9' },
                { value: 'incomeClass.10' },
                { value: 'incomeClass.11' },
                { value: 'incomeClass.12' },
                { value: 'incomeClass.13' },
              ]}
            />
          </FieldSet>
        </ConditionalSection>

        <FieldSet localeId="background.labels.currentStatus">
          <FormField
            name="currentStatus"
            component={CheckboxGroup}
            optionFormatter={formatOpt}
            options={[
              { value: 'fullTime' },
              { value: 'partTime' },
              { value: 'student' },
              { value: 'sickLeave' },
              { value: 'unemployedOrLaidOff' },
              { value: 'retired' },
              { value: 'conscription' },
              { value: 'homemaker' },
            ]}
          />
        </FieldSet>
        <ConditionalSection condition={has('currentStatus', 'partTime')}>
          <FieldSet localeId="background.labels.partTimeWorkedHours">
            <FormField
              name="partTimeWorkedHours"
              component={RadioButtonGroup}
              optionFormatter={formatOpt}
              options={[
                { value: 'none' },
                { value: 'lessThan20' },
                { value: '20_30' },
                { value: '31_35' },
                { value: '36_40' },
                { value: 'moreThan40' },
              ]}
            />
          </FieldSet>
        </ConditionalSection>

        <ConditionalSection condition={has('currentStatus', 'sickLeave')}>
          <FieldSet localeId="background.labels.sickLeaveDaysPassed">
            <FormField
              name="sickLeaveDaysPassed"
              component={NumberFieldBox}
              hintLocale="background.labels.sickLeaveDaysPassedHint"
            />
          </FieldSet>
        </ConditionalSection>

        <ConditionalSection condition={has('currentStatus', 'unemployedOrLaidOff')}>
          <FieldSet localeId="background.labels.unemployedDaysPassed">
            <FormField
              name="unemployedDaysPassed"
              component={NumberFieldBox}
              hintLocale="background.labels.unemployedDaysPassedHint"
            />
          </FieldSet>
        </ConditionalSection>

        <ConditionalSection condition={has('currentStatus', 'retired')}>
          <FieldSet localeId="background.labels.retirementType">
            <FormField
              name="retirementType"
              component={RadioButtonGroup}
              optionFormatter={formatOpt}
              options={[
                { value: 'disabilityPensionOrEarlyRetirement' },
                { value: 'partTimePension' },
                { value: 'oldAgePension' },
                { value: 'unemploymentPension' },
              ]}
            />
          </FieldSet>
          <ConditionalSection condition={fieldIs('retirementType', 'partTimePension')}>
            <FieldSet localeId="background.labels.partTimePensionWorkedHours">
              <FormField
                name="partTimePensionWorkedHours"
                component={RadioButtonGroup}
                optionFormatter={formatOpt}
                options={[
                  { value: 'none' },
                  { value: 'lessThan20' },
                  { value: '20_30' },
                  { value: '31_35' },
                  { value: '36_40' },
                  { value: 'moreThan40' },
                ]}
              />
            </FieldSet>
          </ConditionalSection>
          <ConditionalSection condition={oneOf('retirementType', ['oldAgePension', 'unemploymentPension'])}>
            <FieldSet localeId="background.labels.occupationBeforePension">
              <FormField
                name="occupationBeforePension"
                component={RadioButtonGroup}
                optionFormatter={formatOpt}
                options={[
                  { value: 'employee' },
                  { value: 'entrepreneur' },
                  { value: 'farmer' },
                  { value: 'selfEmployedOrFreelancer' },
                  { value: 'noEmploymentBefore' },
                ]}
              />
            </FieldSet>
          </ConditionalSection>
        </ConditionalSection>
        <ConditionalSection
          condition={either(
            hasOne('currentStatus', ['fullTime', 'partTime', 'sickLeave', 'unemployedOrLaidOff']),
            both(
              has('currentStatus', 'retired'),
              oneOf('retirementType', ['disabilityPensionOrEarlyRetirement', 'partTimePension']),
            ),
          )}
        >
          <FieldSet localeId="background.labels.occupation">
            <FormField
              name="occupation"
              component={CheckboxGroup}
              optionFormatter={formatOpt}
              options={[
                { value: 'employee' },
                { value: 'entrepreneur' },
                { value: 'farmer' },
                { value: 'selfEmployedOrFreelancer' },
                { value: 'noEmploymentBefore' },
              ]}
            />
          </FieldSet>
          <ConditionalSection condition={has('occupation', 'employee')}>
            <FieldSet localeId="background.labels.statusAsEmployee">
              <FormField
                name="statusAsEmployee"
                component={RadioButtonGroup}
                optionFormatter={formatOpt}
                options={[
                  { value: 'management' },
                  { value: 'specialist' },
                  { value: 'expert' },
                  { value: 'officeWorker' },
                  { value: 'servicesAndSales' },
                  { value: 'constructionAndManufacturing' },
                  { value: 'processAndLogistics' },
                  { value: 'soldier' },
                  { value: 'otherEmployee' },
                ]}
              />
            </FieldSet>
            <ConditionalSection condition={fieldIs('statusAsEmployee', 'otherEmployee')}>
              <FieldSet localeId="background.labels.statusAsEmployeeOther">
                <FormField
                  name="otherEmployeeDetail"
                  component={TextFieldBox}
                  hintLocale="background.labels.statusAsEmployeeOtherHint"
                />
              </FieldSet>
            </ConditionalSection>
          </ConditionalSection>
          <ConditionalSection
            condition={either(
              complement(has('currentStatus', 'retired')),
              fieldIs('retirementType', 'partTimePension'),
            )}
          >
            <FieldSet localeId="background.labels.workAbilityComparedToLifetimeBest">
              <FormField
                name="workAbilityScore.workAbilityComparedToLifetimeBest"
                component={RadioButtonGroup}
                optionFormatter={opt => formatOpt(`workAbilityComparedToLifetimeBest.${opt}`)}
                options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(o => ({ value: o }))}
              />
            </FieldSet>
          </ConditionalSection>
          <ConditionalSection condition={lte('workAbilityComparedToLifetimeBest', 7)} path={['workAbilityScore']}>
            <FieldSet localeId="background.labels.reductionReason">
              <FormField
                name="workAbilityScore.reductionReason"
                component={TextFieldArea}
                hintLocale="background.labels.reductionReasonHint"
              />
            </FieldSet>
            <FieldSet localeId="background.labels.reductionType">
              <FormField
                name="workAbilityScore.reductionType"
                component={RadioButtonGroup}
                optionFormatter={opt => formatOpt(`reductionType.${opt}`)}
                options={[{ value: 'temporary' }, { value: 'permanent' }, { value: 'unknown' }]}
              />
            </FieldSet>
          </ConditionalSection>
          <ConditionalSection condition={hasOne('currentStatus', ['fullTime', 'partTime'])}>
            <FieldSet localeId="background.labels.selfAssessmentAbilityToWorkIn2Years">
              <FormField
                name="selfAssessmentAbilityToWorkIn2Years"
                component={RadioButtonGroup}
                optionFormatter={opt => formatOpt(`selfAssessmentAbilityToWorkIn2Years.${opt}`)}
                options={['1', '4', '7'].map(o => ({ value: o }))}
              />
            </FieldSet>
          </ConditionalSection>
        </ConditionalSection>
      </FormSection>
    </React.Fragment>
  );
};

const BackgroundForm = ({ session, diagnoses, ...props }) => (
  <FormProvider {...props}>
    <DiagnosisDisplay diagnoses={diagnoses} />
    <Form {...{ session, ...props }} />
  </FormProvider>
);

BackgroundForm.propTypes = {
  session: PropTypes.object,
  diagnoses: PropTypes.array,
};

const mapStateToProps = state => ({
  session: state.session,
  diagnoses: state.neuroDocuments.documents
    .filter(doc => doc.documentType === 'diagnosis')
    .map(doc => getLatestFinishedCommitData(doc)),
});

export default withRouter(connect(mapStateToProps, null)(BackgroundForm));
